import type { KeycloakInitOptions } from 'keycloak-js';

import { getKeycloakRedirectUri } from './keycloakRedirect';
import { loadPersistedKeycloakSession } from './keycloakTokenStorage';

/** Hub portal intent passed to Keycloak as the `portal` OIDC query param. */
export type KeycloakPortal = 'customer' | 'employee';

/** OIDC scope for hub login — offline refresh tokens on web and native. */
export const KEYCLOAK_DEFAULT_SCOPE = 'openid offline_access';

/** @deprecated Use {@link KEYCLOAK_DEFAULT_SCOPE}. */
export const KEYCLOAK_NATIVE_SCOPE = KEYCLOAK_DEFAULT_SCOPE;

/** @deprecated Use {@link KEYCLOAK_DEFAULT_SCOPE}. Same offline scope on every platform. */
export const KEYCLOAK_WEB_SCOPE = KEYCLOAK_DEFAULT_SCOPE;

/** OIDC scope used for Keycloak login and OTP verify (always includes offline_access). */
export function getKeycloakDefaultScope(): string {
	return KEYCLOAK_DEFAULT_SCOPE;
}

export type KeycloakInitOptionsConfig = {
	/** When true, do not inject persisted tokens (required during authorization-code callback handling). */
	skipPersistedTokens?: boolean;
};

/** Builds `keycloak-js` init options for web or native, including restored tokens when available. */
export function getKeycloakInitOptions(
	options: KeycloakInitOptionsConfig = {},
): KeycloakInitOptions {
	const persisted = options.skipPersistedTokens ? null : loadPersistedKeycloakSession();

	return {
		pkceMethod: 'S256',
		checkLoginIframe: false,
		redirectUri: getKeycloakRedirectUri(),
		scope: getKeycloakDefaultScope(),
		// Query mode keeps callbacks in `?code=` (not `#code=`), which is reliable with Vue history routing.
		responseMode: 'query',
		...(persisted
			? {
					token: persisted.token,
					refreshToken: persisted.refreshToken,
					idToken: persisted.idToken,
				}
			: {}),
	};
}
